import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Monitor, Printer, Phone, Network, Wrench, Shield, Database, Globe,
  CheckCircle2, ArrowLeft, ArrowRight, FileText, ChevronDown, ChevronUp 
} from 'lucide-react';
import { services } from '../data/mock';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import Header from '../components/Header';
import Footer from '../components/Footer';

const iconMap = {
  Monitor: Monitor,
  Printer: Printer,
  Phone: Phone,
  Network: Network,
  Wrench: Wrench,
  Shield: Shield,
  Database: Database,
  Globe: Globe
};

// Affiche le prix avec la mention "HT" en plus petit (en conservant un éventuel suffixe, ex: "/mois")
const renderPrice = (text) => {
  if (typeof text === 'string' && text.includes(' HT')) {
    const idx = text.indexOf(' HT');
    const before = text.slice(0, idx);
    const after = text.slice(idx + 3);
    return (
      <>
        {before}
        <span className="text-[0.6em] font-semibold opacity-70 ml-0.5">HT</span>
        {after}
      </>
    );
  }
  return text;
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const service = services.find(s => s.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  
  if (!service) {
    return (
      <div className="min-h-screen bg-white">
        <Helmet defer={false}>
          <title>Service non trouvé | LYNKO</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Service non trouvé</h1>
          <p className="text-gray-600 mb-8">Le service que vous recherchez n'existe pas.</p>
          <Button onClick={() => navigate('/')} className="bg-blue-500 hover:bg-blue-600">
            Retour à l'accueil
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const IconComponent = iconMap[service.icon];
  
  // Trouver les services suivant et précédent
  const currentIndex = services.findIndex(s => s.slug === slug);
  const prevService = currentIndex > 0 ? services[currentIndex - 1] : null;
  const nextService = currentIndex < services.length - 1 ? services[currentIndex + 1] : null;

  return (
    <div className="min-h-screen bg-white">
      <Helmet defer={false}>
        <title>{`${service.title} | LYNKO`}</title>
        <meta name="description" content={service.description} />
        <link rel="canonical" href={`https://lynko.info/services/${service.slug}`} />
        <meta property="og:title" content={`${service.title} | LYNKO`} />
        <meta property="og:description" content={service.description} />
        <meta property="og:url" content={`https://lynko.info/services/${service.slug}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": service.title,
          "description": service.description,
          "url": `https://lynko.info/services/${service.slug}`,
          "provider": { "@type": "ProfessionalService", "name": "LYNKO", "url": "https://lynko.info/" }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://lynko.info/" },
            { "@type": "ListItem", "position": 2, "name": service.title, "item": `https://lynko.info/services/${service.slug}` }
          ]
        })}</script>
      </Helmet>
      <Header />
      
      {/* Hero Section */}
      <section className={`bg-gradient-to-r ${service.bgGradient} py-16 lg:py-24`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            onClick={() => window.scrollTo(0, 0)}
          >
            <ArrowLeft className="w-4 h-4" />
            Retour aux services
          </Link>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className={`w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6`}>
                <IconComponent className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">{service.title}</h1>
              <p className="text-lg lg:text-xl text-white/90 leading-relaxed mb-8">
                {service.fullDescription}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  className="bg-white text-gray-900 hover:bg-gray-100 font-semibold px-6 py-3 rounded-lg flex items-center gap-2 h-auto"
                  onClick={() => { navigate('/contact'); window.scrollTo(0, 0); }}
                >
                  <FileText className="w-5 h-5" />
                  Demander un devis
                </Button>
                <Button 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 font-semibold px-6 py-3 rounded-lg flex items-center gap-2 h-auto"
                  onClick={() => { navigate('/contact'); window.scrollTo(0, 0); }}
                >
                  <Phone className="w-5 h-5" />
                  Nous contacter
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fonctionnalités détaillées */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Ce que nous proposons
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {`Découvrez en détail les prestations incluses dans notre service ${service.title.toLowerCase()}`}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.detailedFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className={`w-12 h-12 ${service.color} rounded-lg flex items-center justify-center mb-4`}>
                  <CheckCircle2 className={`w-6 h-6 ${service.iconColor}`} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tarification */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Nos tarifs
            </h2>
            <p className="text-lg text-gray-600">
              Des formules adaptées à tous les besoins
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {service.pricing.map((plan, index) => {
              const highlighted = index === 1;
              return (
                <div 
                  key={index}
                  className={`rounded-2xl p-6 flex flex-col ${highlighted ? `bg-gradient-to-br ${service.bgGradient} text-white` : 'bg-gray-50 border border-gray-200'}`}
                >
                  <h3 className={`text-xl font-bold mb-4 ${highlighted ? 'text-white' : 'text-gray-900'}`}>
                    {plan.name}
                  </h3>

                  {plan.items ? (
                    <div className="space-y-2 mb-4">
                      {plan.items.map((it, i) => (
                        <div 
                          key={i} 
                          className={`flex items-baseline justify-between gap-3 border-b pb-2 ${highlighted ? 'border-white/20' : 'border-gray-200'}`}
                        >
                          <span className={`text-sm ${highlighted ? 'text-white/80' : 'text-gray-600'}`}>{it.label}</span>
                          <span className={`font-bold whitespace-nowrap ${highlighted ? 'text-white' : service.iconColor}`}>{renderPrice(it.value)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <p className={`text-3xl font-bold mb-4 ${highlighted ? 'text-white' : service.iconColor}`}>
                        {renderPrice(plan.price)}
                      </p>
                      <p className={`text-sm mb-6 ${highlighted ? 'text-white/80' : 'text-gray-600'}`}>
                        {plan.description}
                      </p>
                    </>
                  )}

                  {plan.note && (
                    <p className={`text-xs mb-6 ${highlighted ? 'text-white/70' : 'text-gray-500'}`}>
                      {plan.note}
                    </p>
                  )}

                  <Button 
                    className={`w-full mt-auto ${highlighted ? 'bg-white text-gray-900 hover:bg-gray-100' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                    onClick={() => { navigate('/contact'); window.scrollTo(0, 0); }}
                  >
                    Demander un devis
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Questions fréquentes
            </h2>
            <p className="text-lg text-gray-600">
              Tout ce que vous devez savoir sur notre service
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {service.faq.map((item, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white rounded-xl border border-gray-200 px-6"
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-4">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-4">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`bg-gradient-to-r ${service.bgGradient} py-16`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Prêt à passer à l'action ?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Contactez-nous dès maintenant pour obtenir un devis personnalisé
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              className="bg-white text-gray-900 hover:bg-gray-100 font-semibold px-8 py-3 rounded-lg h-auto"
              onClick={() => navigate('/#contact')}
            >
              Demander un devis gratuit
            </Button>
          </div>
        </div>
      </section>

      {/* Navigation entre services */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {prevService ? (
              <Link 
                to={`/services/${prevService.slug}`}
                className="flex items-center gap-3 text-gray-600 hover:text-blue-500 transition-colors group"
              >
                <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                <div className="text-left">
                  <p className="text-sm text-gray-400">Service précédent</p>
                  <p className="font-semibold">{prevService.title}</p>
                </div>
              </Link>
            ) : <div />}
            
            {nextService ? (
              <Link 
                to={`/services/${nextService.slug}`}
                className="flex items-center gap-3 text-gray-600 hover:text-blue-500 transition-colors group"
              >
                <div className="text-right">
                  <p className="text-sm text-gray-400">Service suivant</p>
                  <p className="font-semibold">{nextService.title}</p>
                </div>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceDetail;
